import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { inboundRentalsAPI } from '../lib/inboundRentalsApi';
import {
  conditionsDepuis,
  corpsDepuisConditions,
  erreursConditions,
} from '../lib/inboundRentalsForm';
import { messageErreur } from '../lib/erreurs';
import { formatDate } from '../lib/formats';
import { libelle, referenceLisible, ton } from '../lib/libelles';
import { cn } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../components/ui/sonner';
import InboundConditionsFields from '../components/rentals/InboundConditionsFields';
import InboundPaymentsPanel from '../components/rentals/InboundPaymentsPanel';
import InboundStatementPanel from '../components/rentals/InboundStatementPanel';
import NumerotationEtats from '../components/rentals/NumerotationEtats';
import TransportStatementsPanel from '../components/rentals/TransportStatementsPanel';
import InboundTrucksPanel, {
  aujourdhuiIso,
  situationCamion,
} from '../components/rentals/InboundTrucksPanel';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

const ONGLETS = [
  { value: 'camions', label: 'Camions du contrat' },
  { value: 'conditions', label: 'Conditions' },
  // Un contrat est réglé au mois (relevés) ou à l'état (états de paiement du transport): un seul des deux onglets.
  { value: 'releves', label: 'Relevés mensuels', moneyOnly: true, reglement: 'MENSUEL' },
  { value: 'etats', label: 'États de paiement', moneyOnly: true, reglement: 'ETAT' },
  { value: 'paiements', label: 'Paiements', moneyOnly: true },
];

/**
 * Le parcours de l'exploitant: une société loue des camions → les envoie
 * charger → vend le carburant → encaisse. Cet écran est la première étape;
 * les suivantes sont à un clic.
 */
const ETAPES = [
  { cle: 'camions', libelle: 'Camions loués', to: null },
  { cle: 'chargement', libelle: 'Chargement', to: '/trips/new' },
  { cle: 'vente', libelle: 'Vente', to: '/orders' },
  { cle: 'encaissement', libelle: 'Encaissement', to: '/finance' },
];

export const Parcours = ({ className }) => (
  <nav
    aria-label="Parcours de l’exploitation"
    className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground', className)}
  >
    <span className="font-semibold uppercase tracking-wide">Parcours</span>
    {ETAPES.map((etape, index) => (
      <React.Fragment key={etape.cle}>
        {index > 0 ? <ArrowRight className="h-3 w-3" aria-hidden="true" /> : null}
        {etape.to ? (
          <Link to={etape.to} className="font-medium text-foreground underline-offset-2 hover:underline">
            {etape.libelle}
          </Link>
        ) : (
          <span className="rounded-md bg-primary/10 px-1.5 py-0.5 font-semibold text-primary" aria-current="step">
            {etape.libelle}
          </span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

/** La phrase qui dit quoi faire ensuite, selon l'état du contrat. */
export const prochaineEtape = (contrat) => {
  if (!contrat) return null;
  if (contrat.status === 'CLOS') return 'Contrat clos : les camions sont rendus à leur propriétaire.';
  if ((contrat.trucks_count ?? (contrat.trucks || []).length) === 0) {
    return 'Ajoutez les camions loués à ce transporteur, puis activez le contrat.';
  }
  if (contrat.status === 'BROUILLON') {
    return 'Vérifiez les conditions, puis activez le contrat : ses camions pourront alors partir charger.';
  }
  if (contrat.settlement_method === 'ETAT') {
    return 'Contrat en cours : envoyez les camions charger, puis établissez un état de paiement avec les trajets dépotés.';
  }
  return 'Contrat en cours : envoyez les camions charger, puis établissez le relevé du mois à la fin du mois.';
};

const InboundRentalContractPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canSeeMoney, isSuperAdmin, isAdminSociete } = useAuth();
  const estAdmin = Boolean(isSuperAdmin || isAdminSociete);
  const [searchParams, setSearchParams] = useSearchParams();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [account, setAccount] = useState(null);

  const [form, setForm] = useState(() => conditionsDepuis(null));
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busy, setBusy] = useState('');
  const [dialog, setDialog] = useState(null); // 'cloturer' | 'supprimer' | 'activer'
  const [closeDate, setCloseDate] = useState('');
  const [closeReason, setCloseReason] = useState('');

  const charger = useCallback(async () => {
    try {
      const data = await inboundRentalsAPI.get(id);
      setContract(data);
      setForm(conditionsDepuis(data));
      setFailed(false);
      return data;
    } catch (error) {
      setFailed(true);
      toast.error(messageErreur(error, 'Impossible de charger ce contrat'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  const chargerCompte = useCallback(async () => {
    if (!canSeeMoney) return;
    try {
      setAccount(await inboundRentalsAPI.account(id));
    } catch {
      setAccount(null);
    }
  }, [id, canSeeMoney]);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    chargerCompte();
  }, [chargerCompte]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['locations', 'camions', 'depenses', 'finance'], (options) => Promise.all([charger(options), chargerCompte(options)]));


  const actualiser = useCallback(async () => {
    await Promise.all([charger(), chargerCompte()]);
  }, [charger, chargerCompte]);

  const reglement = contract?.settlement_method || 'MENSUEL';
  const onglets = ONGLETS.filter(
    (onglet) => (!onglet.moneyOnly || canSeeMoney) && (!onglet.reglement || onglet.reglement === reglement),
  );
  const demande = searchParams.get('onglet');
  const actif = onglets.some((onglet) => onglet.value === demande) ? demande : 'camions';
  const choisirOnglet = (valeur) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('onglet', valeur);
        return next;
      },
      { replace: true },
    );

  const situations = useMemo(() => {
    const aujourdhui = aujourdhuiIso();
    const compte = { EN_COURS: 0, RENDU: 0, A_VENIR: 0 };
    (contract?.trucks || []).forEach((ligne) => {
      compte[situationCamion(ligne, aujourdhui)] += 1;
    });
    return compte;
  }, [contract]);

  const setField = (nom, valeur) => setForm((prev) => ({ ...prev, [nom]: valeur }));
  const setPayer = (categorie, porteur) =>
    setForm((prev) => ({ ...prev, payer_matrix: { ...prev.payer_matrix, [categorie]: porteur } }));

  const options = { canSeeMoney, nouveau: false };
  const erreurs = erreursConditions(form, options);

  const enregistrerConditions = async () => {
    setSubmitAttempted(true);
    if (Object.values(erreurs).some(Boolean)) {
      toast.error('Complétez les champs signalés');
      return;
    }
    setBusy('conditions');
    try {
      await inboundRentalsAPI.update(contract.id, corpsDepuisConditions(form, options));
      toast.success('Conditions enregistrées');
      setSubmitAttempted(false);
      await actualiser();
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement des conditions impossible'));
    } finally {
      setBusy('');
    }
  };

  const activer = async () => {
    setBusy('activer');
    try {
      await inboundRentalsAPI.activate(contract.id);
      toast.success('Contrat activé');
      setDialog(null);
      await actualiser();
    } catch (error) {
      setDialog(null);
      toast.error(messageErreur(error, 'Activation impossible'));
    } finally {
      setBusy('');
    }
  };

  const cloturer = async () => {
    setBusy('cloturer');
    try {
      await inboundRentalsAPI.close(contract.id, { endDate: closeDate || undefined, reason: closeReason.trim() });
      toast.success('Contrat clôturé : les camions sont rendus à leur propriétaire');
      setDialog(null);
      await actualiser();
    } catch (error) {
      setDialog(null);
      toast.error(messageErreur(error, 'Clôture impossible'));
    } finally {
      setBusy('');
    }
  };

  const supprimer = async () => {
    setBusy('supprimer');
    try {
      await inboundRentalsAPI.remove(contract.id);
      toast.success('Brouillon supprimé');
      navigate('/rentals?onglet=entrantes', { replace: true });
    } catch (error) {
      setDialog(null);
      toast.error(messageErreur(error, 'Suppression impossible'));
    } finally {
      setBusy('');
    }
  };

  const ouvrirCloture = () => {
    setCloseDate(aujourdhuiIso());
    setCloseReason('');
    setDialog('cloturer');
  };

  if (loading && !contract) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <PageHeader
          eyebrow="Location reçue"
          title="Contrat introuvable"
          backTo="/rentals?onglet=entrantes"
          backLabel="Retour aux locations"
        />
        <EmptyState
          variant="error"
          title={failed ? 'Le contrat n’a pas pu être chargé' : 'Contrat introuvable'}
          description="Ce contrat n’existe pas ou n’appartient pas à votre société. Revenez à la liste pour en choisir un autre."
          actionLabel="Voir les locations"
          onAction={() => navigate('/rentals?onglet=entrantes')}
        />
      </div>
    );
  }

  const brouillon = contract.status === 'BROUILLON';
  const clos = contract.status === 'CLOS';
  const nbCamions = contract.trucks_count ?? (contract.trucks || []).length;

  return (
    <div
      className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8"
      data-testid="inbound-rental-contract-page"
    >
      <PageHeader
        eyebrow="Location reçue"
        title={contract.lessor_name || 'Transporteur'}
        backTo="/rentals?onglet=entrantes"
        backLabel="Retour aux locations"
        stats={[
          { label: 'Camions', valeur: nbCamions, aide: 'Camions du contrat' },
          {
            label: 'Dans votre flotte',
            valeur: situations.EN_COURS,
            ton: 'positif',
            aide: 'Dans votre flotte aujourd’hui',
          },
          {
            label: libelle('truckStatus', 'RETURNED_TO_OWNER'),
            valeur: situations.RENDU,
            aide:
              situations.A_VENIR > 0
                ? `${situations.A_VENIR} pas encore commencé${situations.A_VENIR > 1 ? 's' : ''}`
                : undefined,
          },
          ...(canSeeMoney
            ? [
                {
                  label: 'Reste à payer',
                  valeur: account?.balance,
                  unite: 'FCFA',
                  ton: Number(account?.balance) > 0 ? 'attention' : 'neutre',
                  aide: 'Reste à payer au propriétaire',
                  onClick: () => choisirOnglet('paiements'),
                },
              ]
            : []),
        ]}
        meta={
          <>
            <span>{`Contrat ${referenceLisible(contract.contract_number)} · ${libelle('mode_remuneration_location', contract.mode)}`}</span>
            <Badge variant={ton('statut_contrat_entrant', contract.status)} data-testid="contrat-etat">
              {libelle('statut_contrat_entrant', contract.status)}
            </Badge>
            <span>
              Du {formatDate(contract.start_date)} au {contract.end_date ? formatDate(contract.end_date) : 'sans terme'}
            </span>
            <span>Chauffeur : {libelle('fournisseur_chauffeur', contract.driver_provider).toLowerCase()}</span>
          </>
        }
        actions={
          <>
            {brouillon ? (
              <>
                <Button variant="outline" onClick={() => setDialog('supprimer')} data-testid="delete-inbound-btn">
                  Supprimer le brouillon
                </Button>
                <Button
                  onClick={() => setDialog('activer')}
                  disabled={nbCamions === 0}
                  title={nbCamions === 0 ? 'Ajoutez d’abord au moins un camion' : undefined}
                  data-testid="activate-inbound-btn"
                >
                  Activer le contrat
                </Button>
              </>
            ) : null}
            {contract.status === 'ACTIF' ? (
              <Button variant="outline" onClick={ouvrirCloture} data-testid="close-inbound-btn">
                Clôturer le contrat
              </Button>
            ) : null}
          </>
        }
      />

      {/* Le parcours à gauche, « Prochaine étape » à droite sur la même ligne ; dessous sous 640 px. */}
      <div
        className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
        data-testid="parcours-ligne"
      >
        <Parcours className="min-w-0" />
        <Aide libelle="Prochaine étape" titre="Prochaine étape" align="end">
          {prochaineEtape(contract)}
        </Aide>
      </div>

      <Tabs value={actif} onValueChange={choisirOnglet} className="space-y-4">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          {onglets.map((onglet) => (
            <TabsTrigger key={onglet.value} value={onglet.value} data-testid={`inbound-tab-${onglet.value}`}>
              {onglet.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="camions" className="mt-0">
          <InboundTrucksPanel contract={contract} canSeeMoney={canSeeMoney} onChanged={actualiser} />
        </TabsContent>

        <TabsContent value="conditions" className="mt-0 space-y-4">
          {clos ? (
            <p className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
              Ce contrat est clos : ses conditions ne peuvent plus être modifiées.
              {contract.close_reason ? ` Motif : ${contract.close_reason}.` : ''}
            </p>
          ) : null}
          <InboundConditionsFields
            form={form}
            setField={setField}
            setPayer={setPayer}
            canSeeMoney={canSeeMoney}
            idPrefix="conditions"
            errors={erreurs}
            submitAttempted={submitAttempted}
            disabled={clos}
            numerotation={
              estAdmin ? <NumerotationEtats contractId={contract.id} disabled={clos} onChanged={actualiser} /> : null
            }
          />
          {!clos ? (
            <div className="flex justify-end">
              <Button onClick={enregistrerConditions} disabled={busy === 'conditions'} data-testid="save-conditions-btn">
                {busy === 'conditions' ? 'Enregistrement…' : 'Enregistrer les conditions'}
              </Button>
            </div>
          ) : null}
        </TabsContent>

        {canSeeMoney && reglement === 'MENSUEL' ? (
          <TabsContent value="releves" className="mt-0">
            <InboundStatementPanel contract={contract} onChanged={actualiser} />
          </TabsContent>
        ) : null}

        {canSeeMoney && reglement === 'ETAT' ? (
          <TabsContent value="etats" className="mt-0">
            <TransportStatementsPanel contract={contract} onChanged={actualiser} />
          </TabsContent>
        ) : null}

        {canSeeMoney ? (
          <TabsContent value="paiements" className="mt-0">
            <InboundPaymentsPanel contract={contract} onChanged={actualiser} />
          </TabsContent>
        ) : null}
      </Tabs>

      <AlertDialog open={dialog === 'activer'} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activer ce contrat ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les {nbCamions} camion{nbCamions > 1 ? 's' : ''} du contrat entrent dans votre flotte aux dates prévues.
              Vous pourrez encore ajuster les dates de chaque camion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Pas maintenant</AlertDialogCancel>
            <AlertDialogAction onClick={activer} disabled={busy === 'activer'} data-testid="confirm-activate">
              Activer le contrat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialog === 'supprimer'} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce brouillon ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le contrat et la liste de ses camions seront supprimés. Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Garder le brouillon</AlertDialogCancel>
            <AlertDialogAction onClick={supprimer} disabled={busy === 'supprimer'} data-testid="confirm-delete">
              Supprimer le brouillon
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialog === 'cloturer'} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clôturer ce contrat ?</AlertDialogTitle>
            <AlertDialogDescription>
              Après la date de fin, les camions du contrat sont « {libelle('truckStatus', 'RETURNED_TO_OWNER')} » : ils
              ne peuvent plus être affectés à un trajet. Un mois déjà validé ne peut pas être écourté.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cloture-date">Dernier jour de location</Label>
              <Input
                id="cloture-date"
                type="date"
                value={closeDate}
                onChange={(event) => setCloseDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cloture-motif">Motif</Label>
              <Textarea
                id="cloture-motif"
                rows={2}
                value={closeReason}
                placeholder="Facultatif"
                onChange={(event) => setCloseReason(event.target.value)}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={cloturer} disabled={busy === 'cloturer'} data-testid="confirm-close">
              Clôturer le contrat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InboundRentalContractPage;
